import Button from '@rio-cloud/rio-uikit/Button';
import ExpanderList from '@rio-cloud/rio-uikit/ExpanderList';
import { FunctionComponent, useId } from 'react';
import { FormattedMessage } from 'react-intl';
import { runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import { assertExhaustive } from '../../../../../utils';
import { OptionalLabel } from '../../../sharedComponents/OptionalLabel';
import { getServiceNameByType } from '../../serviceOrders/serviceOrderUtils';
import { HighlightedFormContainer } from '../../shared/sidebar/HighlightedFormContainer';
import { ServiceDescription } from '../../shared/sidebar/ServiceDescription';
import { Service, ServiceOption, ServiceOptionStatus } from '../serviceOption.types';
import {
    ExecutionDateErrorType,
    SidebarFormFieldError,
    useServiceOptionCreateOrderForm,
} from './useServiceOptionCreateOrderForm';
import { DayAndTimePicker } from '../../../sharedComponents/DayAndTimePicker';
import { ValidationResult } from '../../../sharedComponents/validationResult.type';

export const SERVICE_COUNT_BADGE_TEST_ID = 'SERVICE_COUNT_BADGE_TEST_ID';

type ServiceOptionSidebarFormProps = {
    serviceOption: ServiceOption;
};

export const ServiceOptionSidebarForm: FunctionComponent<ServiceOptionSidebarFormProps> = ({ serviceOption }) => {
    const isExpanderOpen = serviceOption.services.length === 1;

    return (
        <HighlightedFormContainer instructionMessageId="outboundPortal.serviceOption.sidebar.pleaseProvide">
            <div className="margin-top-10 margin-bottom-10">
                <FormattedMessage id="outboundPortal.serviceOption.sidebar.services" />
            </div>
            <ExpanderList
                items={serviceOption.services.map((service) => ({
                    id: service.type,
                    open: isExpanderOpen,
                    header: (
                        <div>
                            <span className="rioglyph rioglyph-component-custom padding-right-5" />
                            {service.type} – {getServiceNameByType(service.type)}
                            {service.executionCount > 0 && (
                                <span
                                    data-testid={SERVICE_COUNT_BADGE_TEST_ID}
                                    className="badge badge-primary margin-left-5"
                                >
                                    {service.executionCount}
                                </span>
                            )}
                        </div>
                    ),
                    body: <CreateServiceOrderForm openedService={service} serviceOption={serviceOption} />,
                }))}
                className=""
                unmountOnExit={false}
            />
        </HighlightedFormContainer>
    );
};

type CreateServiceOrderFormProps = {
    openedService: Service;
    serviceOption: ServiceOption;
};

// eslint-disable-next-line max-lines-per-function
const CreateServiceOrderForm: FunctionComponent<CreateServiceOrderFormProps> = ({ serviceOption, openedService }) => {
    const isDisabled = serviceOption.status !== ServiceOptionStatus.OPEN;
    const generalInformationId = useId();

    const {
        formData,
        setFormData,
        formErrors,
        setDateParseError,
        hasDateParseError,
        handleCreateServiceOrder,
        isCreationLoading,
    } = useServiceOptionCreateOrderForm(serviceOption, openedService);

    return (
        <>
            <div className="margin-bottom-20">
                <ServiceDescription service={openedService} />
            </div>

            <div className="display-flex align-items-start flex-row flex-wrap gap-10 margin-bottom-20">
                <div className="flex-1-1 max-width-50pct">
                    <DayAndTimePicker
                        labelId="outboundPortal.serviceOrder.sidebar.executionStartDate"
                        timeZone={serviceOption.serviceLocation.timeZone}
                        date={formData.startedAt}
                        validation={getDateValidation(formErrors.startedAt)}
                        onDateChange={(startedAt) =>
                            setFormData((current) => ({
                                ...current,
                                startedAt,
                            }))
                        }
                        isDisabled={isDisabled}
                        onParseError={(hasParseError) => setDateParseError('startedAt', hasParseError)}
                    />
                </div>

                <div className="flex-1-1 max-width-50pct">
                    <DayAndTimePicker
                        labelId="outboundPortal.serviceOrder.sidebar.executionEndDate"
                        timeZone={serviceOption.serviceLocation.timeZone}
                        date={formData.completedAt}
                        validation={getDateValidation(formErrors.completedAt)}
                        onDateChange={(completedAt) =>
                            setFormData((current) => ({
                                ...current,
                                completedAt,
                            }))
                        }
                        isDisabled={isDisabled}
                        onParseError={(hasParseError) => setDateParseError('completedAt', hasParseError)}
                    />
                </div>
            </div>
            <div className="margin-bottom-20">
                <OptionalLabel isOptional htmlFor={generalInformationId}>
                    <FormattedMessage id="outboundPortal.serviceOrder.sidebar.generalInformation" />
                </OptionalLabel>
                <textarea
                    id={generalInformationId}
                    disabled={isDisabled}
                    className="width-100pct form-control"
                    style={{ resize: 'none' }}
                    rows={5}
                    maxLength={255}
                    value={formData.generalInformation ?? ''}
                    onChange={(text) => {
                        setFormData({
                            ...formData,
                            generalInformation: text.target.value,
                        });
                    }}
                />
            </div>
            <div className="display-flex gap-10 flex-row-reverse">
                <Button
                    iconName="rioglyph-plus"
                    bsStyle={Button.PRIMARY}
                    onClick={runInBackgroundCallback(handleCreateServiceOrder)}
                    disabled={isDisabled || isCreationLoading || hasDateParseError}
                >
                    <FormattedMessage id="outboundOrderBook.serviceOption.sidebar.createServiceOrder" />
                </Button>
            </div>
        </>
    );
};

const getDateValidation = (error: SidebarFormFieldError<ExecutionDateErrorType> | undefined): ValidationResult => {
    if (error === undefined) {
        return { error: undefined, warning: undefined };
    }

    switch (error.type) {
        case 'dateInFuture':
            return {
                error: 'outboundPortal.serviceOrder.sidebar.executionDate.inTheFuture',
                warning: undefined,
            };
        case 'endDateBeforeStartDateError':
            return {
                error: 'outboundPortal.serviceOrder.sidebar.executionDate.endDateBeforeStartDateError',
                warning: undefined,
            };
        case 'oneMandatory':
            return {
                error: 'outboundPortal.serviceOrder.sidebar.executionDate.oneMandatory',
                warning: undefined,
            };
        default:
            return assertExhaustive(error.type);
    }
};
